import { useEffect, useMemo, useState } from "react"
import { downloadAttachment } from "./services/download"
import { ChevronLeft, ChevronRight, CornerUpLeft, Download, ExternalLink, Share2, Trash2, X, ZoomIn, ZoomOut } from "lucide-react"
import { ShareDialog } from "@/clientWeb/components/ui/ShareDialog"
import { useAttachmentSrcState } from "./hooks/useAttachmentSrcState"
import { openAttachmentInTab } from "./services/private-attachment"

export interface PreviewImageItem {
  id: string
  url: string
  name: string
  /** Images and video share this viewer — a video attachment used to have no
   *  preview at all, so tapping one did nothing that images did. */
  kind?: "image" | "video"
  /** Whether the viewer sent this. Download is offered to recipients only. */
  isSentByMe?: boolean
  /** The message this attachment arrived in, when the caller knows it. */
  messageId?: string
}

interface ImagePreviewOverlayProps {
  open: boolean
  images: PreviewImageItem[]
  currentIndex: number
  onClose: () => void
  onChangeIndex: (index: number) => void
  onDeleteCurrent?: (id: string) => void
  /** Jump to the message this item arrived in. Omitted, the button is not shown. */
  onShowInChat?: (id: string) => void
  /** False when the caller asks its own confirmation, so the user is not asked twice. */
  confirmDelete?: boolean
  /** Translated label for the Show in chat button. */
  showInChatLabel?: string
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

export function ImagePreviewOverlay({
  open,
  images,
  currentIndex,
  onClose,
  onChangeIndex,
  onDeleteCurrent,
  onShowInChat,
  confirmDelete = true,
  showInChatLabel = "Show in chat",
}: ImagePreviewOverlayProps) {
  const [zoom, setZoom] = useState(1)
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const safeIndex = useMemo(
    () => clamp(currentIndex, 0, Math.max(0, images.length - 1)),
    [currentIndex, images.length],
  )
  const currentImage = images[safeIndex]
  const canNavigate = images.length > 1
  const isVideo = currentImage?.kind === "video"
  /**
   * The bytes, fetched with this client's token.
   *
   * Attachment URLs live behind `requireAuth`, and a media element cannot send
   * an Authorization header — there is no API for it. So `<img src>` and
   * `<video src>` went out bare and came back 401: a broken image, and a video
   * that rendered its frame and would not play. The thread's own bubbles have
   * always resolved them this way; this viewer was pointing straight at the
   * raw URL. Called before the early return below, because hooks must be.
   *
   * The state, not just the src: a private chat file whose link the backend
   * refuses (VAPT 2026-10) left `undefined` behind, which this viewer drew as
   * "Loading…" for ever.
   */
  const { src: mediaSrc, status: mediaStatus, retry: retryMedia } = useAttachmentSrcState(currentImage?.url)
  // Sender-side download is noise: they chose the file off their own disk and
  // still have it. The control belongs to whoever received it.
  const canDownload = !currentImage?.isSentByMe

  useEffect(() => {
    if (!open) return
    setZoom(1)
  }, [open, safeIndex])

  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose()
        return
      }

      if (!canNavigate) return

      if (event.key === "ArrowLeft") {
        onChangeIndex(clamp(safeIndex - 1, 0, images.length - 1))
      }
      if (event.key === "ArrowRight") {
        onChangeIndex(clamp(safeIndex + 1, 0, images.length - 1))
      }
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [open, canNavigate, onClose, onChangeIndex, safeIndex, images.length])

  if (!open || !currentImage) return null

  // `link.download` is ignored for cross-origin URLs, and `target="_blank"`
  // then turned the click into "navigate to the image" — leaving the chat.
  // Fetch the bytes and download from a same-origin blob instead.
  const onDownload = () => {
    void downloadAttachment(currentImage.url, currentImage.name || "image")
  }

  const onShare = () => {
    setShareDialogOpen(true)
  }

  const onDeleteClick = () => {
    if (!confirmDelete) { onDeleteCurrent?.(currentImage.id); return }
    setDeleteConfirmOpen(true)
  }

  const onDeleteConfirm = () => {
    setDeleteConfirmOpen(false)
    onDeleteCurrent?.(currentImage.id)
  }

  // A private chat file's URL names it but does not open it: the tab gets a
  // link signed for this user, or the CDN's MissingKey page is all it shows.
  const onOpenExternal = () => {
    openAttachmentInTab(currentImage.url)
  }

  return (
    // `pointer-events-auto` is load-bearing. This overlay is opened from
    // inside a Radix drawer (the contact info panel), and an open Radix
    // drawer sets `pointer-events: none` on the body so only its own content
    // is clickable. This lives outside that subtree, so every control in here
    // — the X included — quietly stopped responding whenever the preview had
    // been opened from the panel rather than from the thread. Hence "the
    // close button sometimes does not work".
    <div className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm pointer-events-auto">
      <div className="flex h-full flex-col">
        <header className="flex h-14 items-center justify-between border-b border-stroke-100/5 px-4 text-white">
          <div className="min-w-0 pr-4 text-sm font-medium truncate">{currentImage.name}</div>
          <div className="flex items-center gap-1.5">
            {/* Zooming a video is meaningless — it has its own controls. */}
            {!isVideo && (
              <>
                <button
                  type="button"
                  onClick={() => setZoom((z) => clamp(z - 0.2, 0.6, 3))}
                  className="grid h-8 w-8 place-items-center rounded-md hover:bg-white/15"
                  title="Zoom out"
                >
                  <ZoomOut className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setZoom((z) => clamp(z + 0.2, 0.6, 3))}
                  className="grid h-8 w-8 place-items-center rounded-md hover:bg-white/15"
                  title="Zoom in"
                >
                  <ZoomIn className="h-4 w-4" />
                </button>
              </>
            )}
            {canDownload && (
              <button
                type="button"
                onClick={onDownload}
                className="grid h-8 w-8 place-items-center rounded-md hover:bg-white/15"
                title="Download"
              >
                <Download className="h-4 w-4" />
              </button>
            )}
            {onShowInChat && (
              <button
                type="button"
                onClick={() => onShowInChat(currentImage.id)}
                className="grid h-8 w-8 place-items-center rounded-md hover:bg-white/15"
                title={showInChatLabel}
              >
                <CornerUpLeft className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={onShare}
              className="grid h-8 w-8 place-items-center rounded-md hover:bg-white/15"
              title="Share"
            >
              <Share2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onOpenExternal}
              className="grid h-8 w-8 place-items-center rounded-md hover:bg-white/15"
              title="Open in new tab"
            >
              <ExternalLink className="h-4 w-4" />
            </button>
            {/* Only when the caller can delete. Without a handler the button
                confirmed and then did nothing, e.g. on a group's picture. */}
            {onDeleteCurrent && (
              <button
                type="button"
                onClick={onDeleteClick}
                className="grid h-8 w-8 place-items-center rounded-md hover:bg-red-500/30"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="grid h-8 w-8 place-items-center rounded-md hover:bg-white/15"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="relative min-h-0 flex-1 overflow-hidden">
          <button
            type="button"
            onClick={onClose}
            className="absolute inset-0 h-full w-full cursor-default"
            aria-label="Close preview"
          />

          {canNavigate && (
            <button
              type="button"
              onClick={() => onChangeIndex(clamp(safeIndex - 1, 0, images.length - 1))}
              disabled={safeIndex <= 0}
              className="absolute left-4 top-1/2 z-[1] grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-black/35 text-white transition hover:bg-black/50 disabled:opacity-30"
              title="Previous"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}

          <div className="absolute inset-0 grid place-items-center p-6">
            {/* `max-h-full`/`h-full` resolve against the ancestor chain, and a
                percentage height that fails to resolve leaves the image at its
                intrinsic size — a 1170x2532 phone screenshot then overflows and
                is clipped. The vh cap needs no ancestor height, so it always
                bounds the image. */}
            {mediaStatus === "failed" ? (
              <div className="relative flex flex-col items-center gap-3 text-center text-white/80">
                <span className="text-sm">{isVideo ? "This video can't be opened right now." : "This image can't be opened right now."}</span>
                <button
                  type="button"
                  onClick={retryMedia}
                  className="rounded-md border border-white/30 px-3 py-1.5 text-xs hover:bg-white/15"
                >
                  Try again
                </button>
              </div>
            ) : !mediaSrc ? (
              // Resolving the bytes takes a round trip. Rendering the element
              // with no src in the meantime draws a broken-image icon, which
              // says "this file is broken" rather than "this is loading".
              <div className="flex flex-col items-center gap-3 text-white/70">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/25 border-t-white/80" />
                <span className="text-xs">Loading…</span>
              </div>
            ) : isVideo ? (
              <video
                key={currentImage.url}
                src={mediaSrc}
                controls
                autoPlay
                playsInline
                // Stop the backdrop behind from taking the click: the scrub bar
                // and the play button are inside this box, and a click that
                // closed the preview instead of seeking would make the player
                // unusable.
                onClick={(e) => e.stopPropagation()}
                className="max-h-full max-w-full object-contain"
                style={{ maxHeight: 'calc(100vh - 9rem)' }}
              />
            ) : (
              <img
                src={mediaSrc}
                alt={currentImage.name}
                className="max-h-full max-w-full object-contain transition-transform duration-150"
                style={{
                  transform: `scale(${zoom})`,
                  maxHeight: 'calc(100vh - 9rem)',
                }}
              />
            )}
          </div>

          {canNavigate && (
            <button
              type="button"
              onClick={() => onChangeIndex(clamp(safeIndex + 1, 0, images.length - 1))}
              disabled={safeIndex >= images.length - 1}
              className="absolute right-4 top-1/2 z-[1] grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-black/35 text-white transition hover:bg-black/50 disabled:opacity-30"
              title="Next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="h-8 border-t border-white/15 px-4 text-xs text-white/70 flex items-center justify-center">
          {safeIndex + 1} / {images.length}
        </div>
      </div>

      <ShareDialog
        isOpen={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        url={currentImage.url}
        title={isVideo ? "Share Video" : "Share Image"}
      />

      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 animate-in fade-in duration-200">
          <div className="w-full max-w-sm bg-card rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200 mx-4 p-6">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="h-16 w-16 rounded-full bg-red-500/10 flex items-center justify-center">
                <Trash2 className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">{isVideo ? "Delete Video" : "Delete Image"}</h3>
              <p className="text-sm text-muted-foreground">{isVideo ? "Would you like to delete this video?" : "Would you like to delete this image?"}</p>
              <div className="flex items-center gap-3 mt-2 w-full">
                <button
                  type="button"
                  onClick={() => setDeleteConfirmOpen(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onDeleteConfirm}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
